if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        // The notify button is name="klaviyo-button" in main-product, c-main-product and c-featured-product; "bis-button" kept as a fallback.
        this.klaviyoBtn = this.querySelector('[name="klaviyo-button"], [name="bis-button"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        // 2026-09-23: the Boomi live-stock check took 1.7 s of every add (measured: the drawer opened 4.1 s after the
        // click, 1.74 s of it waiting for boomiws before /cart/add was even sent). Ask early instead, on the signals
        // that a shopper is about to buy - a variant change, the pointer or focus entering the buy form, a touch on
        // it - and keep the answer for 30 s per SKU + quantity, so the click usually finds it ready. Nothing is asked
        // on page load, so Boomi sees no traffic from visitors who only look. The gate itself is unchanged.
        this.stockCache = null;
        const prefetch = () => { setTimeout(() => this.prefetchStock(), 0); };
        const scope = this.closest('.shopify-section') || document;
        scope.addEventListener('on:variant:change', prefetch);
        this.form.addEventListener('pointerenter', prefetch);
        this.form.addEventListener('touchstart', prefetch, { passive: true });
        this.form.addEventListener('focusin', prefetch);
      }
    }

    /**
     * Key of the current stock question: the SKU and quantity the form would submit.
     * @returns {?string}
     */
    stockKey() {
      if (!this.form || this.form.classList.contains('pre-order-form')) return null;
      const sku = this.form.querySelector('[name="sku"]');
      const qty = this.form.querySelector('[name="quantity"]');
      if (!sku || !sku.value) return null;
      return `${sku.value}|${qty ? qty.value : '1'}`;
    }

    /**
     * Starts the Boomi stock check for the current SKU + quantity unless a fresh answer is already cached.
     * @returns {?Promise<boolean>}
     */
    prefetchStock() {
      const key = this.stockKey();
      if (!key) return null;
      if (this.stockCache && this.stockCache.key === key && (Date.now() - this.stockCache.at) < 30000) {
        return this.stockCache.promise;
      }
      const [sku, quantity] = key.split('|');
      const promise = this.getServerStock(this.buildRequestObject([{ sku, quantity }]));
      this.stockCache = { key, at: Date.now(), promise };
      return promise;
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

       let preorder = this.form.classList.contains('pre-order-form');

      // 2026-09-23: the pre-add cart snapshot (for the analytics event further down) starts now and is awaited
      // after the stock check, so it costs nothing instead of ~200 ms on the critical path. It still precedes the
      // add request, so it is the cart as it was before this add.
      const oldCartPromise = fetch(`${theme.routes.cart}.js`);

      if(!preorder){
        let items = [];
        items.push({ sku: formData.get("sku"), quantity: formData.get("quantity") })
        // Uses the prefetched answer when it is for this SKU + quantity and under 30 s old, else asks now.
        const key = `${items[0].sku}|${items[0].quantity}`;
        const cached = this.stockCache && this.stockCache.key === key && (Date.now() - this.stockCache.at) < 30000;
        const stockOk = cached ? await this.stockCache.promise : await this.getServerStock(this.buildRequestObject(items));
        this.stockCache = null;
        if(stockOk == false) {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.removeAttribute('aria-disabled');
            // Umbra card pill (cart page rails): the card has no notify button to swap in, so hiding the pill left an empty slot and
            // threw on the missing button. Say "Not added" in the pill and the reason in #cart-errors instead (assets/main.js).
            if (!this.klaviyoBtn && this.submitBtn.dataset.outOfStock) {
                this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: this.submitBtn.dataset.outOfStock } }));
                return;
            }
            // Swap to the notify button when there is one (it is a no-op, and the buy button stays, when there is none).
            if (this.klaviyoBtn) {
              this.submitBtn.hidden = true;
              this.klaviyoBtn.classList.remove('js-hidden');
              this.klaviyoBtn.hidden = false;
            }
            // 2026-10-05: the message goes in the form's own error box (translated through window.umbraI18n, English
            // fallback inline) instead of a browser alert. A form without that box reports it through the cart error event.
            const stockMsg = (window.umbraI18n && window.umbraI18n.atc_stock_check_failed) || "We couldn't check stock. Please try again.";
            this.setErrorMsgState(stockMsg);
            if (!this.errorMsg) this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: stockMsg } }));
            return;
        } 
      }

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      // R10-D1: a form inside the cart drawer (Others also bought, Complete your order) is replaced when the drawer
      // re-renders, so its events would fire on a detached node and never reach document. They go out from the drawer
      // then, which bubbles to document with the same detail.
      const evtTarget = () => (this.isConnected ? this : (this.cartDrawer && this.cartDrawer.isConnected ? this.cartDrawer : document));

      try {
        const oldCartResponse = await oldCartPromise;
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // 2026-09-23: no 700 ms timer before the drawer renders. The tick and the wordmark loader live on the
          // button, not on the drawer, so the wait only delayed the drawer (measured 760 ms of a 4.1 s open).
          if (this.cartDrawer) {
            // R10-D1: inside the drawer the button is about to be replaced, so the tick shows first and the drawer
            // updates 350 ms later, the same confirmation shoppers get everywhere else.
            if (this.cartDrawer.contains(this)) {
              this.submitBtn.classList.add('is-success');
              await new Promise((resolve) => { setTimeout(resolve, 350); });
            }
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                cartItems.refresh();
              }
            }
          }
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          evtTarget().dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          evtTarget().dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }

        // The tick belongs to a successful add only (a failed one throws before this line).
        this.submitBtn.classList.add('is-success');
      } catch (error) {
        console.log(error); // eslint-disable-line
        evtTarget().dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: (this.errorMsg && !this.errorMsg.hidden) ? this.errorMsg.textContent : ''
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      // A reset must empty the text too: it stays readable in the hidden element and the catch block below reads it, so a
      // later 429 or aborted add reported the previous 422 (Umbra cart rails, 2026-09-30).
      if (!error) this.errorMsg.textContent = '';
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }

   /**
     * Functions for live inventory check
     * Build Request Object 
     * @param {object} items - Items Object
     */
    buildRequestObject(items){
      const store = Shopify.shop.split(".myshopify")[0];

      const req = {
        'store': store,
        'items': []
      }

      for(var i=0; i<items.length; ++i){
        var item = items[i];
        var qty = item.quantity;
        if(qty == undefined || qty == null){
          qty = 1;
        }
        var object = {
          'sku': item.sku,
          'qty': qty
        }
        req.items.push(object);
      }

      return req;
    }

    /**
     * Getting the server stock from boomi
     */

    async getServerStock(request, callback){
      const store = Shopify.shop.split(".myshopify")[0];
      let host = 'boomiws';
      let bp = "85d702a5-e7a2-426d-b35f-034b45730497";
      if(store === 'umbra-dev'){
        host = 'boomiqasws';
        bp = '6006e1b9-0ffb-4e57-ac9d-5e49642185f0';
      }
      const url = `https://${host}.umbra.com/ws/simple/getCart_Inventory?store=${store}`;
      
      let proceed = true;

      // 2026-10-02: a stock call that hangs must not hang the add to cart. After 4 s (more than twice the 1.7 s measured
      // on a normal day) the request is aborted; the abort lands in the catch below, which already fails open.
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 4000) : null;

      const fetchOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Basic " + btoa(`shopify@umbraltd-HV85PQ:${bp}`)
        },
        body: JSON.stringify(request),
        signal: controller ? controller.signal : undefined
      }
      try{
        const boomiResponse = await fetch(url, fetchOptions);
        const boomiData = await boomiResponse.json();

        if(boomiData.status == "FAIL"){
          proceed = false;
          return proceed;
        }
      }catch(error){
        console.log('failing the live inventory check process, but it still should proceed with add to cart !');
        return proceed;
      } finally {
        if (timer) clearTimeout(timer);
      }

      return proceed;
    }
  }

  customElements.define('product-form', ProductForm);
}

// 2026-09-15: this file is included by 16 sections, so a page executes it 2-4 times; classic scripts share one
// global scope and the top-level `let inputs` / `let collapsibles` below threw "Identifier 'inputs' has already
// been declared" on every copy after the first (every page, live). Scoped in a function and run once.
(function () {
  if (window.__umbraProductFormTail) return;
  window.__umbraProductFormTail = true;
// Variant Get Notified
let inputs = document.querySelectorAll('.option-selector__btns input');
if (inputs.length > 1) {
  for (let i = 0; i < inputs.length; i++) {
    inputs[i].addEventListener('click', function() {
      const bis = document.querySelector('.back-in-stock-button');
      const atc = document.querySelector('.add-to-crt-button');
      if (!bis || !atc) return;
      if (inputs[i].classList.contains('is-unavailable')) {
        bis.classList.remove('js-hidden')
        atc.classList.add('js-hidden')
      } else {
        bis.classList.add('js-hidden')
        atc.classList.remove('js-hidden')
      }
    })
  }
}

// Collapsible
let collapsibles = document.querySelectorAll('.product-info details-disclosure');
for (let i = 0; i < collapsibles.length; i++) {
  const h = collapsibles[i].querySelector('h2');
  if (!h) continue;
  if (h.innerText == 'Features') {
    const p = collapsibles[i].querySelector('.disclosure__content p');
    if (p) p.remove();
  } else if (h.innerText == 'Description') {
    collapsibles[i].querySelectorAll('.disclosure__content li').forEach(function(element){
      element.remove();
    });
  }
}
})();
